import React, { useContext, useMemo } from 'react';
import { useLocation, Link, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';

import Title2 from '@joinforma/ui/components/Title2';
import { ScreenWidth } from '@joinforma/ui/constants/breakpoints';

import { EVENTS } from 'constants/events';
import { FeatureFlag as FLAGS } from 'constants/featureFlag';
import FeatureFlagContext from 'contexts/FeatureFlagContext';
import { VendorRoutes } from 'utils/routeGenerator';
import { logRudderstackEvent } from 'utils/rudderstack';

import type { Product } from '../types';
import { SIZE_OPTIONS, StyleOptions } from './consts';
import Details from './Details';
import Image from './Image';
import Price from './Price';
import OldCard from './ProductCard';

const Card = styled(Link)`
  background-color: ${({ theme }) => theme.colors.background.primary};
  border-radius: 6px;
  display: flex;
  flex: 0 0 auto;
  flex-direction: column;
  gap: 12px;
  position: relative;
  width: ${({ size = SIZE_OPTIONS.LARGE }: { size?: string }): string =>
    StyleOptions[size].width};

  @media (width: ${ScreenWidth.LAPTOP}) {
    width: 207px;
  }

  @media (width: ${ScreenWidth.TABLET_PORTRAIT}) {
    width: 100%;
  }
`;

const PriceVaries = styled(Title2)``;

const ProductCard = ({
  algoliaProduct,
  company,
  currency,
  exchangeRate = 1,
  onClick,
  origin,
  size = SIZE_OPTIONS.SMALL,
}: {
  algoliaProduct: Product;
  company: {
    features: {
      stipend_config: {
        display_as_amount: boolean;
      };
    };
  };
  currency: string;
  exchangeRate?: number;
  onClick?: () => void;
  origin: string;
  size?: string;
}) => {
  const { pathname, search } = useLocation();
  const ff = useContext(FeatureFlagContext);
  const [searchParams] = useSearchParams();
  const hasNewVersionFlag =
    ff.flag(FLAGS.NEW_PRODUCT_CARDS, 'off') === 'experiment';
  const collection = searchParams.get('collection');

  const {
    coupon_code_available: couponCodeAvailable,
    coupon_code_locations: couponCodeLocations,
    product_id: productID,
    vendor_id: vendorID,
  } = algoliaProduct;

  const experiment = useMemo(
    () => [
      `collection: ${collection}`,
      `featureFlag: ${hasNewVersionFlag}`,
      `origin: ${origin}`,
      `pathname: ${pathname}`,
      `productID: ${productID}`,
      `search: ${search}`,
      `vendorID: ${vendorID}`,
    ],
    [
      collection,
      hasNewVersionFlag,
      origin,
      pathname,
      productID,
      search,
      vendorID,
    ]
  );

  const handleClick = () => {
    onClick && onClick();
    logRudderstackEvent(EVENTS.ProductCardClicked, { experiment });
  };

  if (!hasNewVersionFlag) {
    return (
      <OldCard
        algolia_product={algoliaProduct}
        company={company}
        currency={currency}
        exchange_rate={exchangeRate}
        onClick={handleClick}
        origin={origin}
      />
    );
  }

  const doesPriceVary =
    couponCodeAvailable &&
    couponCodeLocations &&
    couponCodeLocations.length > 0;

  return (
    <Card
      onClick={handleClick}
      to={VendorRoutes.detail({
        product_id: productID,
        vendor_id: vendorID,
      })}
      size={size}
      state={{ origin, prev: `${pathname}${search}` }}
    >
      <Image product={algoliaProduct} size={size} />
      <Details product={algoliaProduct} size={size} />
      {doesPriceVary ? (
        <PriceVaries>Price varies by location</PriceVaries>
      ) : (
        <Price
          company={company}
          currency={currency}
          exchangeRate={exchangeRate}
          product={algoliaProduct}
        />
      )}
    </Card>
  );
};

export default ProductCard;
